<template>
  <v-row v-if="showEnable" no-gutters>
    <v-col>
      <v-switch
        v-model="fieldValue"
        :label="showGroupSubgroup"
        :disabled="isBusy || isReadOnly || isDisconnected"
        :loading="isLoading"
        color="primary"
        :error-messages="error_msg"
        @update:model-value="writeValue"
      />
    </v-col>
    <ConfigInputButtons
      :default-value="defaultValue"
      :is-loading="isLoading"
      :is-reload-disabled="isReloadDisabled"
      :is-set-default-disabled="isSetDefaultDisabled"
      :parameter="props.parameter"
      :show-buttons="props.showButtons"
      @reload-value="reloadValue"
      @set-default="setDefault"
    />
  </v-row>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from "vue";
import ConfigInputButtons from "../ConfigInputButtons.vue";
import { ParamField } from "../../enums/ParamField";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { ModbusParam } from "@/types/ModbusParam";
import { TimerId } from "@/types/TimerId";

const modbusStore = useModbusStore();

const props = defineProps<{
  parameter: ModbusParam;
  showButtons?: boolean;
  showGroup?: boolean;
  modbusReload?: boolean;
}>();

let reloadCount = 0;
let write_count = 0;
let write_value: number[] = [];

const modbusReqTimer = ref<TimerId>(0);
const fieldValue = ref<boolean>(false);

const showGroupSubgroup = computed(() => {
  if (props.showGroup) {
    if (
      props.parameter.sub_group === "0" ||
      props.parameter.sub_group === "" ||
      !props.parameter.sub_group
    ) {
      return `${props.parameter.name} (${props.parameter.group})`;
    } else {
      return `${props.parameter.name} (${props.parameter.group}/${props.parameter.sub_group})`;
    }
  } else {
    return props.parameter.name;
  }
});

const isLoading = computed(() => {
  if (
    props.parameter[ParamField.mbusAddress] in modbusStore.modbusAddrReadBusy
  ) {
    return modbusStore.modbusAddrReadBusy[
      props.parameter[ParamField.mbusAddress]
    ];
  } else {
    return false;
  }
});

const error_msg = computed(() => {
  return modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]];
});

const showEnable = computed(() => {
  return (
    props.parameter.input_type === "switch" &&
    modbusStore.isParamUserReadable(props.parameter)
  );
});

const defaultValue = computed<string>(() => {
  if (parseInt(props.parameter[ParamField.default]) === 0) {
    return "false";
  } else {
    return "true";
  }
});

const isDefault = computed(() => {
  const defValue: boolean = parseInt(props.parameter[ParamField.default]) !== 0;
  return defValue === fieldValue.value;
});

const isReloadDisabled = computed<boolean>(
  () => isBusy.value || isReadOnly.value,
);

const isSetDefaultDisabled = computed<boolean>(
  () => isBusy.value || isDefault.value || isReadOnly.value,
);

const isReadOnly = computed(
  () => !modbusStore.isParamUserWriteable(props.parameter),
);

const isDisconnected = computed<boolean>(
  () => modbusStore.portState === PortState.disconnected,
);

const isBusy = computed(
  () =>
    modbusStore.modbusAddrReadBusy[props.parameter[ParamField.mbusAddress]] ||
    modbusStore.modbusAddrWriteBusy[props.parameter[ParamField.mbusAddress]],
);

const modbusAddrReadBusy = computed(() => {
  return modbusStore.modbusAddrReadBusy[
    props.parameter[ParamField.mbusAddress]
  ];
});

const modbusAddrWriteBusy = computed(() => {
  return modbusStore.modbusAddrWriteBusy[
    props.parameter[ParamField.mbusAddress]
  ];
});

function updateValue() {
  if (props.parameter[ParamField.inputType] === "switch") {
    fieldValue.value =
      modbusStore.modbusValues[props.parameter[ParamField.mbusAddress]] === 1;
  }
}

function reloadValue() {
  reloadCount = 0;
  reload();
}

// making these functions async is probably only relevant if either:
// - our caller await()s it
// - we add debugging immediately after it
async function reload() {
  if (modbusStore.storeInit && modbusStore.portState === PortState.connected) {
    if (modbusReqTimer.value !== 0) {
      clearInterval(modbusReqTimer.value);
    }
    //-------- Device Info -------------------------
    await modbusStore.modbusRead(
      modbusStore.deviceAddress,
      props.parameter[ParamField.mbusAddress],
      1,
    );
  }
}

async function writeModbus() {
  await modbusStore.modbusWrite(
    modbusStore.deviceAddress,
    props.parameter[ParamField.mbusAddress],
    1,
    new Uint8Array(write_value),
  );
}

function writeValue(newValue: boolean | null) {
  if (newValue === null) {
    return;
  }
  modbusStore.modbusIsChanged = false;
  if (newValue) {
    write_value = [0x00, 0x01];
  } else {
    write_value = [0x00, 0x00];
  }
  writeModbus();
}

function setDefault() {
  modbusStore.modbusIsChanged = false;
  if (props.parameter[ParamField.default] === "0") {
    write_value = [0x00, 0x00];
  } else {
    write_value = [0x00, 0x01];
  }
  writeModbus();
}

watch(isLoading, (newVal) => {
  if (!newVal) {
    updateValue();
  }
});

watch(modbusAddrReadBusy, (newVal: boolean) => {
  if (!newVal) {
    if (
      modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]] ===
      ""
    ) {
      reloadCount = 0;
    } else {
      if (reloadCount < modbusStore.maxReloadCount) {
        reload();
        reloadCount++;
      }
    }
  }
});

watch(modbusAddrWriteBusy, (newVal: boolean, oldVal: boolean) => {
  if (newVal === false && oldVal !== undefined && write_value.length > 0) {
    if (
      modbusStore.modbusAddrErrMsg[props.parameter[ParamField.mbusAddress]] ==
      ""
    ) {
      write_count = 0;
    } else {
      if (write_count < modbusStore.maxReloadCount) {
        writeModbus();
        write_count++;
      }
    }
  }
});

onMounted(() => {
  if (props.modbusReload) {
    modbusReqTimer.value = setInterval(async () => {
      if (
        modbusStore.storeInit &&
        modbusStore.portState === PortState.connected
      ) {
        reloadValue();
      }
    }, 200);
  } else {
    updateValue();
  }
});

onUnmounted(() => {
  if (modbusReqTimer.value) {
    clearInterval(modbusReqTimer.value);
  }
});
</script>
